let authConfig: {
	getAccessToken?: () => Promise<string | null>;
	instanceId?: string;
	evoOrganisationId?: string;
	companyId?: string;
} = {};

export function configureAuth(config: {
	getAccessToken?: () => Promise<string | null>;
	instanceId?: string;
	evoOrganisationId?: string;
	companyId?: string;
}): void {
	authConfig = { ...authConfig, ...config };
}

export function getAuthConfig(): {
	getAccessToken?: () => Promise<string | null>;
	instanceId?: string;
	evoOrganisationId?: string;
	companyId?: string;
} {
	return authConfig;
}

export function updateCompanyId(companyId: string | undefined): void {
	authConfig = { ...authConfig, companyId };
}

/**
 * Client-side concurrency limiter.
 *
 * Bursts of parallel requests (e.g. committing a what-if scenario, which
 * reassigns several bookings at once, or navigating to the calendar preview,
 * which reloads bookings/absences/date-ranges together) can overwhelm the API
 * and trigger HTTP 429 rate-limit responses faster than the retry/back-off in
 * the service layer can recover from.
 *
 * We cap the number of `fetch` calls that are in flight at any moment and queue
 * the rest, releasing a slot as each request settles. This keeps the request
 * rate below the server's threshold while staying transparent to callers.
 */
const MAX_CONCURRENT_REQUESTS = 6;
let activeRequestCount = 0;
const pendingResolvers: Array<() => void> = [];

function acquireRequestSlot(): Promise<void> {
	if (activeRequestCount < MAX_CONCURRENT_REQUESTS) {
		activeRequestCount++;
		return Promise.resolve();
	}
	return new Promise<void>((resolve) => {
		pendingResolvers.push(resolve);
	});
}

function releaseRequestSlot(): void {
	const next = pendingResolvers.shift();
	if (next) {
		// Hand the freed slot straight to the next waiter — the in-flight count
		// stays the same because the slot is immediately reused.
		next();
	} else {
		activeRequestCount--;
	}
}

/** Test-only helper: number of requests currently occupying a slot. */
export function __getActiveRequestCount(): number {
	return activeRequestCount;
}

export async function authFetch(
	input: RequestInfo | URL,
	init?: RequestInit,
): Promise<Response> {
	const headers = new Headers(init?.headers);

	// Extract URL for logging
	let requestUrl: string;
	if (input instanceof Request) {
		requestUrl = input.url;
	} else if (input instanceof URL) {
		requestUrl = input.toString();
	} else {
		requestUrl = String(input);
	}

	if (authConfig.getAccessToken) {
		try {
			const token = await authConfig.getAccessToken();
			if (token) {
				headers.set("Authorization", `Bearer ${token}`);
			} else {
				console.log(
					"%c[authFetch] No token available for request:",
					"color: orange;",
					{ url: requestUrl },
				);
			}
		} catch (error) {
			console.error("Error getting access token:", error);
		}
	} else {
		console.log(
			"%c[authFetch] No getAccessToken function configured.",
			"color: red;",
		);
	}

	// Always set X-Instance-Id header, with fallback if not configured
	const instanceId = authConfig.instanceId || "default-instance";
	headers.set("X-Instance-Id", instanceId);

	// Set X-Organisation-Id header only when a real value exists (no fallback)
	if (authConfig.evoOrganisationId) {
		headers.set("X-Organisation-Id", authConfig.evoOrganisationId);
	}

	// Set X-Company-Id header for active company selection
	if (authConfig.companyId) {
		headers.set("X-Company-Id", authConfig.companyId);
	}

	await acquireRequestSlot();
	try {
		return await fetch(input, {
			...init,
			headers,
			credentials: 'include',
		});
	} finally {
		releaseRequestSlot();
	}
}
